/**
 * 「이용권 구매 및 관리」 비교표의 기능 항목 ↔ 이용권별 권한 키 묶음.
 * - 관리자 「이용권별 권한 관리」와 비교표(PassPlanComparisonTable)가 같은 정의를 쓴다.
 * - 한 항목이 여러 키를 묶으면 스위치 하나로 모두 함께 켜고 끈다(모두 켜짐일 때만 사용, 저장 시 모두 같은 값으로 정리).
 *   관리자 화면에서 항목을 켜고 끄면 묶인 키를 한꺼번에 바꾼다.
 * - tableDefault 는 권한표를 못 읽었을 때 비교표에 보이는 값(기존 안내 표와 같음).
 */
import {
  defaultPlanFeature,
  type PlanAiFeatureKey,
  type PlanAiFeatureMap,
  type PlanFeatureKey,
  type PlanFeatureMap,
} from './planFeatures';

export type PlanAiColumn = 'learningPlan' | 'worksheet' | 'slides' | 'other';

export type PlanItemBinding =
  /** extra.features 의 기본 기능 키(값이 없으면 키별 기본값) */
  | { kind: 'features'; keys: PlanFeatureKey[] }
  /** AI 열(ai_learning_plan 등). 값이 없으면 꺼짐 */
  | { kind: 'aiColumns'; columns: PlanAiColumn[] }
  /**
   * AI 세부 기능 키(extra.features.ai_*). 관리자 저장·마이그레이션 20261027230000 이 항상 명시 값을 채운다.
   * 값이 없을 때(마이그레이션·저장 전)만 예전 ai_other 열을 따른다.
   */
  | { kind: 'aiFeature'; key: PlanAiFeatureKey };

export type PlanTableItem = {
  id: string;
  label: string;
  /** 관리자 화면 설명(무엇이 막히는지) */
  hint: string;
  binding: PlanItemBinding;
  tableDefault: { free: boolean; pass: boolean };
};

export type PlanTableGroup = { title: string; items: PlanTableItem[] };

const BOTH = { free: true, pass: true };
const PLUS = { free: false, pass: true };
const feat = (...keys: PlanFeatureKey[]): PlanItemBinding => ({ kind: 'features', keys });

/** 표 맨 위 설정 행 중 켜고 끄는 항목(클래스 상한·저장용량·크레딧은 숫자 입력) */
export const PLAN_TABLE_TEAM_ITEM: PlanTableItem = {
  id: 'team',
  label: '팀 만들기 및 팀 가입 기능',
  hint: '팀 학급 만들기·가입 (team_class)',
  binding: feat('team_class'),
  tableDefault: PLUS,
};

export const PLAN_TABLE_GROUPS: PlanTableGroup[] = [
  {
    title: '할일 관리 및 메모 관리',
    items: [
      {
        id: 'todo_daily',
        label: 'todo노트 및 데일리노트',
        hint: 'ToDo 노트 · 데일리 노트 페이지 (todo_note, daily_note)',
        binding: feat('todo_note', 'daily_note'),
        tableDefault: BOTH,
      },
    ],
  },
  {
    title: '과제 관리 및 포인트 관리',
    items: [
      {
        id: 'assignment_general',
        label: '일반 과제',
        hint: '과제 노트 · 대시보드 프레젠 과제 카드 (assignment_note)',
        binding: feat('assignment_note'),
        tableDefault: BOTH,
      },
      {
        id: 'assignment_advanced',
        label: '프로젝트, 루틴, 챌린지 과제',
        hint: '과제 노트에서 프로젝트·루틴·챌린지 만들기 (assignment_advanced)',
        binding: feat('assignment_advanced'),
        tableDefault: PLUS,
      },
      {
        id: 'point_item',
        label: '포인트, 코인, 아이템 기능',
        hint: '포인트 항목 노트 · 대시보드 프레젠 포인트 (point_item_note)',
        binding: feat('point_item_note'),
        tableDefault: BOTH,
      },
    ],
  },
  {
    title: '교육과정 편성 운영 관리',
    items: [
      {
        id: 'dashboard',
        label: '대시보드',
        hint: '대시보드(홈) 화면 (dashboard)',
        binding: feat('dashboard'),
        tableDefault: BOTH,
      },
      {
        id: 'curriculum_basic',
        label: '기본 교육과정 편성 운영 기능',
        hint: '기본 학습내용 등록 · 주간학습 안내 · 예산 노트 (workspace_learning_content, weekly_learning_guide, budget_note)',
        binding: feat('workspace_learning_content', 'weekly_learning_guide', 'budget_note'),
        tableDefault: BOTH,
      },
      {
        id: 'evaluation_plan',
        label: '평가계획 및 관리 기능',
        hint: '기본 평가계획 등록 (workspace_evaluation_plan)',
        binding: feat('workspace_evaluation_plan'),
        tableDefault: BOTH,
      },
      {
        id: 'records',
        label: '기록 및 기록모아보기 기능',
        hint: '수업기록 열 · 행동특성 열 · 기록 모아보기 (class_record, behavior_record, class_record_memos)',
        binding: feat('class_record', 'behavior_record', 'class_record_memos'),
        tableDefault: BOTH,
      },
      {
        id: 'main_presentation',
        label: '메인 프레젠테이션 기능',
        hint: '메인 프레젠테이션 탭 · Shift+O (main_presentation, 예전 키 dashboard_presentation 도 같은 값으로 저장)',
        binding: feat('main_presentation'),
        tableDefault: BOTH,
      },
      {
        id: 'class_alarm',
        label: '수업 알람 기능',
        hint: '상단 종 · 수업 알람 설정·팝업 (class_alarm)',
        binding: feat('class_alarm'),
        tableDefault: BOTH,
      },
      {
        id: 'lesson_presentation',
        label: '수업 프레젠테이션 및 활동지 기능',
        hint: '진도표의 프레젠테이션·활동지 열기 · 자료 등록 (presentation, worksheet, content_registry)',
        binding: feat('presentation', 'worksheet', 'content_registry'),
        tableDefault: PLUS,
      },
      {
        id: 'remote',
        label: '프레젠테이션 리모트 기능',
        hint: '다른 기기 리모트, 서버에서도 차단 (remote_control)',
        binding: feat('remote_control'),
        tableDefault: PLUS,
      },
      {
        id: 'ai_curriculum_input',
        label: 'AI 교육과정 자료 입력 기능',
        hint: 'AI 자료 입력 · 범교과 추출, 서버에서 차단 (ai_curriculum_input)',
        binding: { kind: 'aiFeature', key: 'ai_curriculum_input' },
        tableDefault: PLUS,
      },
      {
        id: 'ai_evaluation',
        label: 'AI 평가계획, 평가기준안, 평가지 생성 기능',
        hint: 'AI 평가 생성, 서버에서 차단 (ai_evaluation)',
        binding: { kind: 'aiFeature', key: 'ai_evaluation' },
        tableDefault: PLUS,
      },
      {
        id: 'ai_generation',
        label: 'AI 학습계획, 프레젠테이션, 활동지 생성 기능',
        hint: 'AI 학습계획·슬라이드·활동지 생성, 서버에서 차단 (ai_learning_plan, ai_slides, ai_worksheet 열)',
        binding: { kind: 'aiColumns', columns: ['learningPlan', 'slides', 'worksheet'] },
        tableDefault: PLUS,
      },
      {
        id: 'ai_record_feedback',
        label: 'AI 행동특성 피드백 기능',
        hint: 'AI 수업기록·평가기록·행동특성 피드백, 서버에서 차단 (ai_record_feedback)',
        binding: { kind: 'aiFeature', key: 'ai_record_feedback' },
        tableDefault: PLUS,
      },
    ],
  },
];

export const PLAN_TABLE_ITEMS: PlanTableItem[] = [PLAN_TABLE_TEAM_ITEM, ...PLAN_TABLE_GROUPS.flatMap((g) => g.items)];

/**
 * 표 항목이 없는 예전 기능 키 → 같은 값으로 저장할 항목 키(읽는 곳이 없어 켜짐 판정에는 쓰지 않음).
 * dashboard_presentation 은 main_presentation 으로 대체됐다.
 */
export const PLAN_FEATURE_MIRRORS: Partial<Record<PlanFeatureKey, PlanFeatureKey>> = {
  dashboard_presentation: 'main_presentation',
};

/** 항목 상태 계산용 입력(권한표 행을 화면마다 맞춰 넣는다) */
export type PlanItemSource = {
  features: Partial<PlanFeatureMap>;
  /** 없으면(읽기 실패) AI 항목 상태를 알 수 없음 → null */
  ai?: Record<PlanAiColumn, boolean>;
  aiFeatures?: PlanAiFeatureMap;
};

/**
 * 항목 켜짐 여부. 묶인 키가 여러 개면 「모두 켜져 있을 때만 켜짐」(일부 상태 없음).
 * 관리자 화면은 저장할 때 묶인 키를 이 값으로 모두 맞춘다.
 * 알 수 없으면(AI 열 읽기 실패) null → 호출부가 tableDefault 사용.
 */
export function planItemOn(item: PlanTableItem, src: PlanItemSource, isFree: boolean): boolean | null {
  const b = item.binding;
  if (b.kind === 'features') {
    return b.keys.every((k) => (typeof src.features[k] === 'boolean' ? (src.features[k] as boolean) : defaultPlanFeature(k, isFree)));
  }
  if (!src.ai) return null;
  const ai = src.ai;
  if (b.kind === 'aiColumns') return b.columns.every((c) => ai[c]);
  const v = src.aiFeatures?.[b.key];
  return typeof v === 'boolean' ? v : ai.other;
}
